import dayjs from 'dayjs';

import {
  NUMBER_OF_TOP_TRIGGERS,
  START_DATE_OF_ALERTS_FEATURE,
  STATUS,
  DAYS_OF_CURRENT_HITS_PERIOD,
  TRIGGER_STATUS
} from '../../constants/alertsConstant';
import requestInstance from '../../utils/requestInstance';
import { CONTEXT_TYPE } from '../../constants/routingStatusConstant';
import router from '../../router';
import { PATH } from '../../constants/common';
import { getPageIndex } from '../../utils/common';
import { sortPrefixByAscending } from '../../utils/ipAddressHelpers';

const state = {
  isLoadingData: false,
  isSendingData: false,
  isDeletingData: false,
  isEditingData: false,
  isSnoozingData: false,
  isUnSnoozingData: false,
  isShowCreateAlertModal: false,
  isAlertHistoryFetched: false,
  isEditingAlert: false,
  isShowSnoozeAlertModal: false,
  isShowDeleteAlertModal: false,
  isShowSuccessInfoBanner: false,
  isShowErrorInfoBanner: false,
  isShowTriggerDetailsModal: false,
  isShowTriggerOfSelectedAlertDetailsModal: false,
  isShowAlertActiveNoticeModal: false,
  isAlertListFetched: false,
  infoBannerTop: 0,
  successInfoBannerText: '',
  errorInfoBannerText: '',
  alertNameQuery: '',
  alertHistoryList: [],
  editingAlertObj: {},
  selectedTriggerWithAlertInfo: {
    trigger: '',
    alertName: '',
    alertId: null
  },
  selectedASN: null,
  alertSummary: {},
  triggers: {},
  selectedTrigger: '',
  firingAlerts: {},
  prefixes: {},
  alerts: {},
  averageHits: {},
  prefixesOfMembers: {},
  memberContacts: {},
  expandedAlertId: null,
  searchQuery: '',
  selectedTriggerASN: null,
  isFetchingPrefixes: false
};

const getters = {
  selectedASNObj: (state, getters, rootState) => {
    return rootState.dataStore.selectedASN;
  },

  alertListOfSelectedMemberId: (state, getters, rootState) => {
    const memberId = rootState.dataStore.selectedAccount?.id;
    if (!memberId) return [];

    return state.alerts[memberId] || [];
  },

  dateOfFirstCreatedAlert: (state, getters) => {
    if (!state.selectedASN || getters.alertListOfSelectedMemberId === [])
      return START_DATE_OF_ALERTS_FEATURE;

    // Set the firstCreatedAlert according to the order of alert id
    const firstCreatedAlert = JSON.parse(
      JSON.stringify(getters.alertListOfSelectedMemberId)
    ).sort((a, b) => a.id - b.id)[0];

    return firstCreatedAlert && firstCreatedAlert.created
      ? firstCreatedAlert.created
      : START_DATE_OF_ALERTS_FEATURE;
  },

  prefixListOfSelectedTriggerASN: (state, getters, rootState) => {
    const memberId = rootState.dataStore.selectedAccount.id;
    const prefixesOfSelectedMember = state.prefixesOfMembers[memberId] || [];

    if (!state.selectedTriggerASN || !state.prefixes[state.selectedTriggerASN])
      return [];

    return state.prefixes[state.selectedTriggerASN].map(prefix => {
      const isBelongingToMember = prefixesOfSelectedMember.includes(prefix);

      return {
        prefix,
        isBelongingToMember
      };
    });
  },

  alertSummaryOfSelectedMemberId: (state, getters, rootState) => {
    const memberId = rootState.dataStore.selectedAccount?.id;
    if (!memberId) return {};

    return state.alertSummary[memberId] || {};
  },

  alertNameListOfSelectedMemberId: (state, getters) => {
    return getters.alertListOfSelectedMemberId.map(alert => alert.name);
  },

  alertObjListOfSelectedMemberId: (state, getters) => {
    return getters.alertSummaryOfSelectedMemberId.alerts || [];
  },

  firingAlertsAmountOfSelectedMemberId: (state, getters, rootState) => {
    const memberId = rootState.dataStore.selectedAccount?.id;

    if (!memberId) return 0;

    return state.firingAlerts[memberId] || 0;
  },

  lastFiringAlertObj: (state, getters) => {
    const lastAlertHistory =
      getters.alertSummaryOfSelectedMemberId['last_firing_alert_week'];

    if (!lastAlertHistory || !lastAlertHistory.name) return {};

    return lastAlertHistory;
  },

  topTriggerList: (state, getters) => {
    const topTriggerList =
      getters.alertSummaryOfSelectedMemberId['top_triggers_week'];

    if (!topTriggerList || !topTriggerList.length) return [];

    return topTriggerList
      .slice(0, NUMBER_OF_TOP_TRIGGERS)
      .map(item => item.trigger);
  },

  selectedTriggerDetails: (state, getters, rootState) => {
    const memberId = rootState.dataStore.selectedAccount?.id;

    if (!memberId || !state.triggers[memberId]) return [];

    if (
      !state.selectedTrigger ||
      !state.triggers[memberId][state.selectedTrigger]
    )
      return [];

    return state.triggers[memberId][state.selectedTrigger];
  },

  selectedTriggerOfAlertDetails: (state, getters, rootState) => {
    const memberId = rootState.dataStore.selectedAccount?.id;
    if (!memberId || !state.triggers[memberId]) return [];

    if (
      !state.selectedTriggerWithAlertInfo.trigger ||
      !state.selectedTriggerWithAlertInfo.alertId ||
      !state.triggers[memberId][state.selectedTriggerWithAlertInfo.trigger]
    )
      return [];

    // Filter by AlertId
    const alertId = state.selectedTriggerWithAlertInfo.alertId;
    let triggerList =
      state.triggers[memberId][state.selectedTriggerWithAlertInfo.trigger];

    triggerList = triggerList.filter(trigger => trigger.alert === alertId);

    return triggerList;
  },

  averageHitOfSelectedASN: state => {
    if (!state.selectedASN) return undefined;

    return state.averageHits[state.selectedASN];
  },

  selectedAccountId: (state, getters, rootState) => {
    return (
      rootState.dataStore.selectedAccount &&
      rootState.dataStore.selectedAccount.id
    );
  },

  memberContactsOfSelectedAccount: (state, getters, rootState, y) => {
    const selectedAccountId = getters.selectedAccountId;
    if (!selectedAccountId) return [];

    let memberContacts = state.memberContacts[selectedAccountId];

    // Remove contact from memberContacts if its uid equals to the userid of current user.
    const userid =
      rootState.authStore &&
      rootState.authStore.user &&
      rootState.authStore.user.userid;

    if (userid)
      memberContacts = memberContacts.filter(item => item.uid !== userid);

    return memberContacts;
  }
};

const actions = {
  // Fetch prefix list of a selected member. It is used when create /edit alert.
  fetchPrefixesByMemberId: (context, memberId) => {
    if (context.state.prefixesOfMembers[memberId] !== undefined) return;

    context.commit('setLoadingData', true);

    requestInstance
      .get(`/prefixes/${memberId}`)
      .then(res => {
        context.commit('setLoadingData', false);
        context.commit('setPrefixesOfMembers', {
          prefixes: res.data,
          memberId
        });
      })
      .catch(err => {
        console.error('fetchPrefixesByMemberId err: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setLoadingData', false);
      });
  },

  // Fetch prefix list of a selected ASN. It is used when create / edit alert.
  fetchPrefixesByASN: (context, asn) => {
    if (!asn || context.state.prefixes[asn] !== undefined) return;

    context.commit('setFetchingPrefixes', true);

    requestInstance
      .get(`/asn/${asn}/prefixes`)
      .then(res => {
        context.commit('setFetchingPrefixes', false);

        if (res.data) {
          context.commit('setPrefixes', { prefixList: res.data, asn });
        }
      })
      .catch(err => {
        console.error('fetchPrefixesByASN err: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setFetchingPrefixes', false);
      });
  },

  createAlert: (context, payload) => {
    context.commit('setSendingData', true);

    const data = {
      snoozeTo: null,
      snoozeFrom: null,
      name: payload.alertName,
      actions: payload.actions,
      filterPrefixes: payload.filterPrefixes,
      memberId: Number(context.getters.selectedAccountId),
      triggerOnFirstHit: payload.alertRule.isTriggeredByFirstHit,
      triggerOnNumHits: payload.alertRule.isTriggeredByHitNumber
        ? parseInt(payload.alertRule.hitNumberPerDay)
        : null,
      alertOnResolve: payload.alertOnResolve
    };

    requestInstance
      .post('/alerts/suspicious-traffic', data)
      .then(res => {
        context.commit('setSendingData', false);
        context.commit('setIsShowAlertActiveNoticeModal', true);
        context.dispatch('clearAlertsCacheByMemberId');
        context.dispatch('fetchAlertListByMemberId');
      })
      .catch(err => {
        console.error('createAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setSendingData', false);
      });
  },

  updateAlert: (context, payload) => {
    context.commit('setEditingData', true);

    const id = context.state.editingAlertObj.id;
    const data = {
      id,
      created: context.state.editingAlertObj.created,
      snoozeTo: context.state.editingAlertObj.snoozeTo,
      snoozeFrom: context.state.editingAlertObj.snoozeFrom,
      name: payload.alertName,
      actions: payload.actions,
      filterPrefixes: payload.filterPrefixes,
      memberId: Number(context.getters.selectedAccountId),
      triggerOnFirstHit: payload.alertRule.isTriggeredByFirstHit,
      triggerOnNumHits: payload.alertRule.isTriggeredByHitNumber
        ? parseInt(payload.alertRule.hitNumberPerDay)
        : null,
      alertOnResolve: payload.alertOnResolve
    };

    requestInstance
      .put('/alerts/suspicious-traffic', data)
      .then(res => {
        context.commit('setEditingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsEditingAlert', false);
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert updated');

        context.dispatch('clearAlertsCacheByMemberId');
        context.dispatch('fetchAlertListByMemberId');
      })
      .catch(err => {
        console.error('updateAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setEditingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsEditingAlert', false);
      });
  },

  snoozeAlert: (context, payload) => {
    context.commit('setSnoozingData', true);

    const id = context.state.editingAlertObj.id;
    const data = {
      id,
      created: context.state.editingAlertObj.created,
      snoozeTo: payload.snoozeTo,
      snoozeFrom: payload.snoozeFrom,
      name: context.state.editingAlertObj.name,
      actions: context.state.editingAlertObj.actions,
      filterPrefixes: context.state.editingAlertObj.filterPrefixes,
      memberId: Number(context.getters.selectedAccountId),
      triggerOnFirstHit: context.state.editingAlertObj.triggerOnFirstHit,
      triggerOnNumHits: context.state.editingAlertObj.triggerOnNumHits,
      alertOnResolve: context.state.editingAlertObj.alertOnResolve
    };

    requestInstance
      .put('/alerts/suspicious-traffic', data)
      .then(res => {
        context.commit('setSnoozingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert snoozed');

        delete context.state.firingAlerts[context.getters.selectedAccountId];
        context.dispatch('clearAlertsCacheByMemberId');
        context.dispatch('fetchAlertListByMemberId');
      })
      .catch(err => {
        console.error('snoozeAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setSnoozingData', false);
        context.commit('setEditingAlertObj', {});
      });
  },

  unSnoozeAlert: context => {
    context.commit('setUnSnoozingData', true);

    const id = context.state.editingAlertObj.id;
    const data = {
      id,
      created: context.state.editingAlertObj.created,
      snoozeTo: null,
      snoozeFrom: null,
      name: context.state.editingAlertObj.name,
      actions: context.state.editingAlertObj.actions,
      filterPrefixes: context.state.editingAlertObj.filterPrefixes,
      memberId: Number(context.getters.selectedAccountId),
      triggerOnFirstHit: context.state.editingAlertObj.triggerOnFirstHit,
      triggerOnNumHits: context.state.editingAlertObj.triggerOnNumHits,
      alertOnResolve: context.state.editingAlertObj.alertOnResolve
    };

    requestInstance
      .put('/alerts/suspicious-traffic', data)
      .then(res => {
        context.commit('setUnSnoozingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert un-snoozed');

        delete context.state.firingAlerts[context.getters.selectedAccountId];
        context.dispatch('clearAlertsCacheByMemberId');
        context.dispatch('fetchAlertListByMemberId');
      })
      .catch(err => {
        console.error('unSnoozeAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setUnSnoozingData', false);
        context.commit('setEditingAlertObj', {});
      });
  },

  deleteAlert: (context, payload) => {
    context.commit('setDeletingData', true);

    const asn = context.state.editingAlertObj.asn;
    requestInstance
      .delete(`/alerts/suspicious-traffic/${payload}`)
      .then(res => {
        context.commit('setDeletingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert deleted');

        delete context.state.firingAlerts[context.getters.selectedAccountId];
        context.dispatch('clearAlertsCacheByMemberId');
        context.dispatch('fetchAlertListByMemberId');
      })
      .catch(err => {
        console.error('deleteAlert error: ', err);

        context.commit('setDeletingData', false);
        context.commit('setEditingAlertObj', {});
      });
  },

  // fetchAlertListByMemberId is the entry of all the alerts APIs, which is triggered when users change "Member account" or operate (edit, snooze, delete) an existing alert
  fetchAlertListByMemberId: context => {
    const memberId = context.rootState.dataStore.selectedAccount?.id;
    const accountName = context.rootState.dataStore.selectedAccount?.account;

    if (!memberId || !accountName) return;

    context.dispatch('fetchFiringAlerts');
    context.dispatch('fetchMemberContactsByMemberAccountName', {
      accountName,
      memberId
    });
    context.commit('setIsAlertListFetched', false);
    context.commit('setLoadingData', true);

    requestInstance
      .get(`/alerts/suspicious-traffic/member/${memberId}`)
      .then(res => {
        context.commit('setLoadingData', false);
        context.commit('setAlerts', { alertList: res.data, memberId });

        context.dispatch('fetchAlertSummary');

        context.dispatch('fetchPrefixesByMemberId', memberId);
        context.commit('setIsAlertListFetched', true);
      })
      .catch(err => {
        console.error('fetchAlertListByMemberId error: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setLoadingData', false);
      });
  },

  fetchFiringAlerts: context => {
    const memberId = context.rootState.dataStore.selectedAccount?.id;
    if (!memberId) return;

    context.commit('setLoadingData', true);

    requestInstance
      .get(`/alerts/suspicious-traffic/firing-alerts/${memberId}`)
      .then(res => {
        context.commit('setLoadingData', false);
        context.commit('setFiringAlerts', {
          firingAlertsAmount: res.data,
          memberId
        });
      })
      .catch(err => {
        console.error('fetchFiringAlerts error: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setLoadingData', false);
      });
  },

  // Fetch alert summary data of a selected ASN. It is used for "Overview" part and the "Your alerts" table
  fetchAlertSummary: (context, asn) => {
    const memberId = context.rootState.dataStore.selectedAccount?.id;
    if (!memberId) return;

    context.commit('setLoadingData', true);

    const end = dayjs().toISOString();
    const start = dayjs(context.getters.dateOfFirstCreatedAlert).toISOString();

    requestInstance
      .get(`/alerts/suspicious-traffic/alert-summary/${memberId}`, {
        params: {
          end,
          start
        }
      })
      .then(res => {
        context.commit('setLoadingData', false);

        res.data &&
          context.commit('setAlertSummary', { memberId, data: res.data });
      })
      .catch(err => {
        console.error('fetchAlertSummary error: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setLoadingData', false);
      });
  },

  fetchHistoryOfSelectedAlert: (context, alertId) => {
    const alertObj =
      context.getters.alertSummaryOfSelectedMemberId.alerts &&
      context.getters.alertSummaryOfSelectedMemberId.alerts.find(
        alert => alert.id === alertId
      );

    // Check whether the history of selected alert has been cached
    if (alertObj && alertObj.isHistoryDataFetched) return;

    // Fetch the history of selected alert
    context.commit('setLoadingData', true);
    const end = dayjs().toISOString();
    const start = dayjs(context.getters.dateOfFirstCreatedAlert).toISOString();

    requestInstance
      .get(`/alerts/suspicious-traffic/history/${alertId}`, {
        params: {
          start,
          end
        }
      })
      .then(res => {
        setTimeout(() => {
          context.commit('setLoadingData', false);
        }, 300);

        if (res.data) {
          // Format the response of alert history data, and sort them by descending order, i.e. from latest to oldest.
          const formattedHistoryList = res.data
            .map(history => ({
              id: `${history.alert}_${history.trigger}_${history.timestamp}`,
              trigger: history.trigger,
              incident: history.reason,
              date: dayjs(history.timestamp).valueOf(),
              timestamp: history.timestamp,
              eventType: history.eventType
            }))
            .sort((a, b) => b.date - a.date);

          context.commit('updateHistoryDataOfSelectedAlert', {
            memberId: context.rootState.dataStore.selectedAccount?.id,
            alertId,
            formattedHistoryList
          });
        }
      })
      .catch(err => {
        console.error('fetchHistoryOfSelectedAlert error:', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setLoadingData', false);
      });
  },

  setErrorInfoBanner: (context, err) => {
    if (err.response && err.response.data && err.response.data.message) {
      context.commit('setErrorInfoBannerText', err.response.data.message);
      context.commit('setIsShowErrorInfoBanner', true);
    } else if (err.message) {
      context.commit('setErrorInfoBannerText', err.message);
      context.commit('setIsShowErrorInfoBanner', true);
    } else {
      context.commit(
        'setErrorInfoBannerText',
        'There was a problem processing your request and we are investigating. Please try again later.'
      );
      context.commit('setIsShowErrorInfoBanner', true);
    }
  },

  fetchTrigger: (context, { trigger, isTriggerOfSelectedAlert }) => {
    const memberId = context.rootState.dataStore.selectedAccount?.id;
    if (!memberId || !trigger) return;

    // Check whether this trigger has been cached in state.triggers
    if (state.triggers[memberId] && state.triggers[memberId][trigger]) {
      // Check whether it is for independent trigger or trigger of an alert
      if (isTriggerOfSelectedAlert) {
        context.commit('setIsShowTriggerOfSelectedAlertDetailsModal', true);
      } else {
        context.commit('setIsShowTriggerDetailsModal', true);
      }
    } else {
      const prefix = encodeURIComponent(trigger);

      context.commit('setLoadingData', true);

      requestInstance
        .get(`/alerts/suspicious-traffic/trigger-history/${memberId}/${prefix}`)
        .then(res => {
          setTimeout(() => {
            context.commit('setLoadingData', false);

            // Check whether it is for independent trigger or trigger of an alert
            if (isTriggerOfSelectedAlert) {
              context.commit(
                'setIsShowTriggerOfSelectedAlertDetailsModal',
                true
              );
            } else {
              context.commit('setIsShowTriggerDetailsModal', true);
            }
          }, 300);

          if (!res.data) return;

          context.commit('updateTriggers', {
            memberId,
            trigger,
            data: res.data
          });
        })
        .catch(err => {
          console.error('fetchTrigger error:', err);
          context.dispatch('setErrorInfoBanner', '');
          context.commit('setLoadingData', false);
        });
    }
  },

  fetchAverageHit: context => {
    const { economy: cc, id: asn } = context.rootState.dataStore.selectedASN;

    if (!cc || !asn) return;

    if (state.averageHits[asn] !== undefined) return state.averageHits[asn];

    const start = dayjs()
      .subtract(DAYS_OF_CURRENT_HITS_PERIOD, 'day')
      .toISOString();
    const end = dayjs().toISOString();

    requestInstance
      .get(`/alerts/suspicious-traffic/average-hits/${cc}`, {
        params: { start, end }
      })
      .then(res => {
        context.commit('setAverageHits', { asn, data: res.data });
      })
      .catch(err => {
        console.error('fetchAverageHit error: ', err);
      });
  },

  fetchMemberContactsByMemberAccountName: (context, payload) => {
    const { accountName, memberId } = payload;

    if (context.state.memberContacts[memberId]) return;

    context.commit('setLoadingData', true);

    requestInstance
      .get(`/alerts/routing-status/contacts/${accountName}`)
      .then(res => {
        context.commit('setLoadingData', false);
        context.commit('setMemberContacts', {
          memberId,
          memberContacts: res.data
        });
      })
      .catch(err => {
        console.error('fetchMemberContactsByMemberAccountName err: ', err);
        context.dispatch('setErrorInfoBanner', '');
        context.commit('setLoadingData', false);
      });
  },

  // Delete the current cached data of selected memberId from state.alertSummary, state.triggers and state.alerts as the data needs to be updated after adding an alert. Without doing it, they won't be updated correctly.
  clearAlertsCacheByMemberId: context => {
    const memberId = context.getters.selectedAccountId;
    if (!memberId) return;

    delete context.state.alertSummary[memberId];
    delete context.state.triggers[memberId];
    delete context.state.alerts[memberId];
  }
};

const mutations = {
  setLoadingData: (state, boolean) => {
    if (boolean) {
      state.isLoadingData = true;
    } else {
      // Use setTimeout to avoid the flickering of loading spinner, as multiple requests are sent when loading this page and they change the isLoadingData status frequently.
      setTimeout(() => {
        state.isLoadingData = false;
      }, 300);
    }
  },

  setSendingData: (state, boolean) => {
    state.isSendingData = boolean;
  },

  setDeletingData: (state, boolean) => {
    state.isDeletingData = boolean;
  },

  setEditingData: (state, boolean) => {
    state.isEditingData = boolean;
  },

  setSnoozingData: (state, boolean) => {
    state.isSnoozingData = boolean;
  },

  setUnSnoozingData: (state, boolean) => {
    state.isUnSnoozingData = boolean;
  },

  setAlerts: (state, payload) => {
    const { alertList, memberId } = payload;

    state.alerts = {
      ...state.alerts,
      [memberId]: alertList
    };
  },

  setIsShowCreateAlertModal: (state, boolean) => {
    state.isShowCreateAlertModal = boolean;
  },

  setIsAlertHistoryFetched: (state, boolean) => {
    state.isAlertHistoryFetched = boolean;
  },

  setAlertHistoryList: (state, alertHistory) => {
    state.alertHistoryList = alertHistory;
  },

  setEditingAlertObj: (state, editingAlertObj) => {
    state.editingAlertObj = editingAlertObj;
  },

  setIsEditingAlert: (state, boolean) => {
    state.isEditingAlert = boolean;
  },

  setIsShowSnoozeAlertModal: (state, boolean) => {
    state.isShowSnoozeAlertModal = boolean;
  },

  setIsShowDeleteAlertModal: (state, boolean) => {
    state.isShowDeleteAlertModal = boolean;
  },

  setSelectedTriggerWithAlertInfo: (state, val) => {
    state.selectedTriggerWithAlertInfo = val;
  },

  setAlertNameQuery: (state, val) => {
    state.alertNameQuery = val;
  },

  setIsShowDetailOnMobile: (state, { alertId, isShowDetail, memberId }) => {
    const alert =
      state.alertSummary[memberId] &&
      state.alertSummary[memberId].alerts &&
      state.alertSummary[memberId].alerts.find(alert => alert.id === alertId);

    alert && (alert.isShowDetail = isShowDetail);
  },

  setIsShowSuccessInfoBanner: (state, boolean) => {
    boolean && (state.infoBannerTop = window.scrollY);
    state.isShowSuccessInfoBanner = boolean;
  },

  setIsShowErrorInfoBanner: (state, boolean) => {
    boolean && (state.infoBannerTop = window.scrollY);
    state.isShowErrorInfoBanner = boolean;
  },

  setSuccessInfoBannerText: (state, msg) => {
    state.successInfoBannerText = msg;
  },

  setErrorInfoBannerText: (state, msg) => {
    state.errorInfoBannerText = msg;
  },

  setSelectedASN: (state, asn) => {
    state.selectedASN = asn;
  },

  // Set state.alertSummary. It caches alertSummary of all ASNs
  setAlertSummary: (state, { asn, data, memberId }) => {
    data.alerts =
      data.alerts &&
      data.alerts.map(alert => ({
        id: alert.id,
        name: alert.name,
        created: alert.created,
        actions: alert.actions,
        filterPrefixes: alert.filterPrefixes,
        memberId: alert.memberId,
        triggerOnFirstHit: alert.triggerOnFirstHit,
        triggerOnNumHits: alert.triggerOnNumHits,
        snoozeFrom: alert.snoozeFrom,
        snoozeTo: alert.snoozeTo,
        status: alert.statuses.alert_status,
        lastTriggerDate: alert.statuses.date_of_last_trigger,
        historyData: [],
        triggerData: Object.keys(alert.statuses.prefix_statuses)
          .map(prefix => ({
            trigger: prefix,
            status: alert.statuses.prefix_statuses[prefix],
            statusOrder: setTriggerStatusOrder(
              alert.statuses.prefix_statuses[prefix]
            )
          }))
          .sort((a, b) => sortPrefixByAscending(a.trigger, b.trigger))
          .sort((a, b) => b.statusOrder - a.statusOrder),
        isShowDetail: false,
        isHistoryDataFetched: false,
        statusOrder: setStatusOrder(alert.statuses.alert_status) // 0: 'Clean', 1: 'Firing', 2: 'Snoozed', 3: 'Fired'
      }));

    // Sort alerts list in descending order by statusOrder, i.e. 'Fired', 'Snoozed', 'Firing', 'Clean'
    data.alerts && data.alerts.sort((a, b) => b.statusOrder - a.statusOrder);

    // It is critical to assign a new value to state.alertSummary here rather than assign a value to state.alertSummary[memberId], which is not able to trigger data update in Vue
    state.alertSummary = { ...state.alertSummary, [memberId]: data };
  },

  updateHistoryDataOfSelectedAlert: (
    state,
    { memberId, alertId, formattedHistoryList }
  ) => {
    const alert =
      state.alertSummary[memberId] &&
      state.alertSummary[memberId].alerts &&
      state.alertSummary[memberId].alerts.find(alert => alert.id === alertId);

    alert && (alert.historyData = formattedHistoryList);

    if (alert) {
      alert.historyData = formattedHistoryList;
      alert.isHistoryDataFetched = true;
    }
  },

  setIsShowTriggerDetailsModal: (state, boolean) => {
    state.isShowTriggerDetailsModal = boolean;
  },

  setIsShowTriggerOfSelectedAlertDetailsModal: (state, boolean) => {
    state.isShowTriggerOfSelectedAlertDetailsModal = boolean;
  },

  updateTriggers: (state, { memberId, trigger, data }) => {
    data.forEach(item => {
      const alert = state.alertSummary[memberId].alerts.find(
        alert => alert.id === item.alert
      );

      if (!alert) return;

      item.alertName = alert.name;
      item.date = item.timestamp;
      item.timestamp = dayjs(item.date).valueOf();
    });

    // Filter out the trigger detail when it belongs to a deleted alert
    data = data.filter(item => item.alertName);

    if (!state.triggers[memberId]) {
      state.triggers = { ...state.triggers, [memberId]: { [trigger]: data } };
    } else {
      state.triggers = {
        ...state.triggers,
        [memberId]: {
          ...state.triggers[memberId],
          [trigger]: data
        }
      };
    }
  },

  setSelectedTrigger: (state, trigger) => {
    state.selectedTrigger = trigger;
  },

  setFiringAlerts: (state, payload) => {
    const { firingAlertsAmount, memberId } = payload;

    state.firingAlerts = {
      ...state.firingAlerts,
      [memberId]: firingAlertsAmount
    };
  },

  setPrefixes: (state, payload) => {
    const { prefixList, asn } = payload;
    state.prefixes = { ...state.prefixes, [asn]: prefixList };
  },

  setPrefixesOfMembers: (state, payload) => {
    const { prefixes, memberId } = payload;
    state.prefixesOfMembers = {
      ...state.prefixesOfMembers,
      [memberId]: prefixes
    };
  },

  setAverageHits: (state, payload) => {
    const { asn, data } = payload;
    state.averageHits = { ...state.averageHits, [asn]: data };
  },

  setIsShowAlertActiveNoticeModal: (state, boolean) => {
    state.isShowAlertActiveNoticeModal = boolean;
  },

  setIsAlertListFetched: (state, boolean) => {
    state.isAlertListFetched = boolean;
  },

  setMemberContacts: (state, payload) => {
    state.memberContacts = {
      ...state.memberContacts,
      [payload.memberId]: payload.memberContacts
    };
  },

  setExpandedAlertId: (state, val) => {
    state.expandedAlertId = val;
  },

  setSearchQuery: (state, val) => {
    state.searchQuery = val;
  },

  setSelectedTriggerASN: (state, asnId) => {
    state.selectedTriggerASN = asnId;
  },

  setFetchingPrefixes: (state, boolean) => {
    state.isFetchingPrefixes = boolean;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};

function setStatusOrder(alertStatus) {
  let statusOrder = 0;

  switch (alertStatus) {
    case STATUS.CLEAN:
      statusOrder = 0;
      break;
    case STATUS.FIRED:
      statusOrder = 1;
      break;
    case STATUS.SNOOZED:
      statusOrder = 2;
      break;
    case STATUS.FIRING:
      statusOrder = 3;
      break;
    default:
      break;
  }

  return statusOrder;
}

function setTriggerStatusOrder(triggerStatus) {
  let statusOrder = 0;

  switch (triggerStatus) {
    case TRIGGER_STATUS.CLEAN:
      statusOrder = 0;
      break;
    case TRIGGER_STATUS.TRIGGERED:
      statusOrder = 1;
      break;
    case TRIGGER_STATUS.TRIGGERING:
      statusOrder = 2;
      break;
    default:
      break;
  }

  return statusOrder;
}

function getPath() {
  return (
    router &&
    router.history &&
    router.history.current &&
    router.history.current.path
  );
}
